import { useEffect, useMemo, useState } from "react";
import { X, Loader2, Send, AlertTriangle, MessageSquarePlus, Globe, FileText } from "lucide-react";
import { toast } from "sonner";
import { watiApi2, chatsApi } from "../lib/api";

/**
 * New Chat modal — mirrors WATI's "Start new conversation" flow.
 *
 * WhatsApp Business policy: once a customer hasn't replied in 24h (or never
 * replied), we can ONLY initiate with a pre-approved template. So the modal:
 *   1. Asks for destination phone + country (India/US).
 *   2. Loads APPROVED templates for that WATI account.
 *   3. Lets the agent pick a template, fill {{1}}..{{N}} placeholders, and
 *      preview the final text.
 *   4. Posts to /api/chats/new which validates, sends via WATI's
 *      sendTemplateMessage, upserts the lead, and logs the outgoing message.
 */
export default function NewChatModal({ open, onClose, onStarted }) {
  const [step, setStep] = useState(1); // 1 = phone/country, 2 = template, 3 = params
  const [country, setCountry] = useState("India");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [templates, setTemplates] = useState([]);
  const [loadingTpl, setLoadingTpl] = useState(false);
  const [selected, setSelected] = useState(null);
  const [params, setParams] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) {
      // Reset
      setStep(1); setPhone(""); setName(""); setCountry("India");
      setTemplates([]); setSelected(null); setParams({});
    }
  }, [open]);

  const loadTemplates = async (cc) => {
    setLoadingTpl(true);
    try {
      const { data } = await watiApi2.templates(cc);
      setTemplates(data?.templates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load WATI templates");
      setTemplates([]);
    } finally {
      setLoadingTpl(false);
    }
  };

  const gotoTemplateStep = async () => {
    const clean = phone.replace(/[^\d+]/g, "").replace(/^\+/, "");
    if (clean.length < 10) {
      toast.error("Enter a valid phone number (with country code, 10+ digits)");
      return;
    }
    setPhone(clean);
    setStep(2);
    await loadTemplates(country);
  };

  const pickTemplate = (t) => {
    setSelected(t);
    const p = {};
    for (let i = 1; i <= (t.placeholders || 0); i++) p[String(i)] = "";
    setParams(p);
    setStep(t.placeholders > 0 ? 3 : 3); // always show preview step
  };

  const previewBody = useMemo(() => {
    if (!selected) return "";
    let body = selected.body || "";
    Object.entries(params).forEach(([k, v]) => {
      body = body.split(`{{${k}}}`).join(v || `{{${k}}}`);
      body = body.split(`{{ ${k} }}`).join(v || `{{${k}}}`);
    });
    return body;
  }, [selected, params]);

  const canSend = useMemo(() => {
    if (!selected) return false;
    // All placeholders must be filled
    for (let i = 1; i <= (selected.placeholders || 0); i++) {
      if (!String(params[String(i)] || "").trim()) return false;
    }
    return true;
  }, [selected, params]);

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const payload = {
        phone,
        name: name.trim() || undefined,
        template_name: selected.name,
        country,
        parameters: Object.entries(params).map(([k, v]) => ({ name: k, value: String(v || "") })),
      };
      const { data } = await chatsApi.newChat(payload);
      toast.success(`New chat started with ${phone}`);
      onStarted?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the chat");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="new-chat-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-[--border]">
          <h2 className="text-base font-semibold text-[--text-primary] flex items-center gap-2">
            <MessageSquarePlus size={18} className="text-[#15803D]" />
            Start New WhatsApp Chat
          </h2>
          <button onClick={onClose} className="text-[--text-muted] hover:text-[--text-primary]" data-testid="new-chat-close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-[12px] text-amber-900">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <p>
                  WhatsApp allows first-contact only through <strong>pre-approved templates</strong>. Pick the destination number + WATI account, then choose a template.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-[--text-primary] mb-1">WATI Account</label>
                <div className="flex gap-2">
                  {["India", "US"].map((cc) => (
                    <button
                      key={cc}
                      onClick={() => setCountry(cc)}
                      data-testid={`new-chat-country-${cc.toLowerCase()}`}
                      className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-md border transition-colors ${
                        country === cc
                          ? "bg-[#312E81] text-white border-[#312E81]"
                          : "bg-white text-[--text-primary] border-[--border] hover:bg-gray-50"
                      }`}
                    >
                      <Globe size={13} /> {cc === "India" ? "🇮🇳 India" : "🇺🇸 US"}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[--text-primary] mb-1">Phone (with country code)</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={country === "India" ? "919876543210" : "14155551234"}
                  className="w-full px-3 py-2 text-sm border border-[--border] rounded-md focus:outline-none focus:ring-2 focus:ring-[#312E81]/30"
                  data-testid="new-chat-phone-input"
                />
                <p className="text-[10px] text-[--text-muted] mt-1">No +, spaces, or dashes. Just digits.</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-[--text-primary] mb-1">Customer Name (optional)</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Ajay Sharma"
                  className="w-full px-3 py-2 text-sm border border-[--border] rounded-md focus:outline-none focus:ring-2 focus:ring-[#312E81]/30"
                  data-testid="new-chat-name-input"
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-[--text-primary]">Choose a template</h3>
                <button onClick={() => setStep(1)} className="text-[11px] text-[--text-muted] underline">
                  ← Change phone/country
                </button>
              </div>
              {loadingTpl ? (
                <div className="flex items-center gap-2 text-sm text-[--text-muted]">
                  <Loader2 size={14} className="animate-spin" /> Loading templates…
                </div>
              ) : templates.length === 0 ? (
                <p className="text-sm text-[--text-muted]">No approved templates found for {country}.</p>
              ) : (
                <ul className="space-y-2 max-h-[50vh] overflow-y-auto" data-testid="new-chat-template-list">
                  {templates.map((t) => (
                    <li key={t.id || t.name}>
                      <button
                        onClick={() => pickTemplate(t)}
                        data-testid={`new-chat-template-${t.name}`}
                        className="w-full text-left p-3 rounded-md border border-[--border] hover:border-[#312E81] hover:bg-[#312E81]/5 transition-colors"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <FileText size={13} className="text-[#312E81]" />
                          <span className="font-medium text-sm text-[--text-primary]">{t.name}</span>
                          {t.language && (
                            <span className="ml-auto text-[10px] uppercase tracking-wider text-[--text-muted]">{t.language}</span>
                          )}
                        </div>
                        <p className="text-xs text-[--text-muted] line-clamp-3 whitespace-pre-wrap">{t.body || "(no body preview)"}</p>
                        {t.placeholders > 0 && (
                          <div className="mt-1 text-[10px] text-[#312E81]">
                            Requires {t.placeholders} parameter{t.placeholders > 1 ? "s" : ""}
                          </div>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {step === 3 && selected && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-[--text-primary]">Fill in & review</h3>
                <button onClick={() => setStep(2)} className="text-[11px] text-[--text-muted] underline">
                  ← Pick another template
                </button>
              </div>
              {selected.placeholders > 0 && (
                <div className="space-y-2">
                  {Array.from({ length: selected.placeholders }, (_, i) => i + 1).map((n) => (
                    <div key={n}>
                      <label className="block text-xs font-medium text-[--text-primary] mb-1">
                        Parameter {"{{"}{n}{"}}"}
                      </label>
                      <input
                        type="text"
                        value={params[String(n)] || ""}
                        onChange={(e) => setParams({ ...params, [String(n)]: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-[--border] rounded-md focus:outline-none focus:ring-2 focus:ring-[#312E81]/30"
                        data-testid={`new-chat-param-${n}`}
                      />
                    </div>
                  ))}
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-[--text-primary] mb-1">Preview</label>
                <div
                  className="p-3 bg-gray-50 border border-[--border] rounded-md text-sm whitespace-pre-wrap text-[--text-primary]"
                  data-testid="new-chat-preview"
                >
                  {previewBody || "(empty)"}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-5 py-3 border-t border-[--border] flex items-center justify-between">
          <div className="text-[10px] text-[--text-muted]">
            {step === 1 && "Step 1 of 3 — Destination"}
            {step === 2 && "Step 2 of 3 — Template"}
            {step === 3 && "Step 3 of 3 — Review & send"}
          </div>
          <div className="flex items-center gap-2">
            {step === 1 && (
              <button
                onClick={gotoTemplateStep}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-md bg-[#312E81] text-white hover:bg-[#25215E]"
                data-testid="new-chat-next-btn"
              >
                Next →
              </button>
            )}
            {step === 3 && (
              <button
                onClick={handleSend}
                disabled={!canSend || sending}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-[#15803D] text-white hover:bg-[#126B33] disabled:opacity-60"
                data-testid="new-chat-send-btn"
              >
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Send Template
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
